import React, { useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

const SortableGrid = ({
  data = [],
  columns = [],
  loading = false,
  emptyMessage = "No data found",
  className = "",
  onRowClick = null,
  expandedRows = {},
  onToggleExpanded = null,
  renderExpandedContent = null,
  // Server-side sorting props
  sortConfig = { key: null, direction: 'asc' },
  onSort = null
}) => {
  // Handle sorting - call server-side sort function
  const handleSort = (key) => {
    if (!onSort) return;
    
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
      // Remove sorting on third click
      onSort(null, null);
      return;
    }
    
    onSort(key, direction);
  };

  // Get sort icon for column
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronsUpDown size={16} className="text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp size={16} className="text-indigo-600" />
      : <ChevronDown size={16} className="text-indigo-600" />;
  };

  // Render cell content
  const renderCell = (item, column) => {
    if (column.render) {
      return column.render(item, column.key);
    }
    return item[column.key];
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-2 md:p-4 ${className}`}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-full">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-2 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    column.sortable !== false 
                      ? 'cursor-pointer hover:bg-gray-100 select-none' 
                      : ''
                  }`}
                  onClick={() => column.sortable !== false && handleSort(column.key)}
                  title={column.sortable !== false ? `Sort by ${column.label}` : ''}
                >
                  <div className="flex items-center gap-1">
                    <span>{column.label}</span>
                    {column.sortable !== false && getSortIcon(column.key)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-2 md:px-6 py-4 md:py-8 text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 md:h-8 w-6 md:w-8 border-b-2 border-indigo-600 mr-2 md:mr-3"></div>
                    <span className="text-gray-600 text-sm md:text-base">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-2 md:px-6 py-4 md:py-8 text-center text-gray-500 text-sm md:text-base">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((item, index) => (
                <React.Fragment key={item.id || index}>
                  <tr 
                    className={`hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''}`}
                    onClick={() => onRowClick && onRowClick(item)}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={`px-2 md:px-6 py-2 md:py-4 whitespace-nowrap text-xs md:text-sm ${
                          column.className || 'text-gray-900'
                        }`}
                      >
                        {renderCell(item, column)}
                      </td>
                    ))}
                  </tr>
                  {/* Expanded Row */}
                  {expandedRows && expandedRows[item.id] && renderExpandedContent && (
                    <tr>
                      <td colSpan={columns.length} className="bg-gray-50 px-2 md:px-6 py-2 md:py-4">
                        {renderExpandedContent(item)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SortableGrid; 